import React, { useState } from "react";
import { SectionCard } from "./common";
import { TField, TSelect } from "./forms";
import { GoldButton, OutlineButton, EmptyState, ui } from "@/components/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Sparkles, FileBarChart, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function PropertyMemory() {
  return (
    <SectionCard className="border border-[#C6A45C]/30">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#F0E7D8] flex items-center justify-center"><Sparkles size={18} className="text-[#C6A45C]" /></div>
        <h3 className="font-display text-xl text-[#2C2A28]">Property Memory</h3>
      </div>
      <p data-testid="property-memory" className="text-sm text-[#8A7F70] leading-relaxed">
        Finetomi is learning the financial history of this property.
      </p>
    </SectionCard>
  );
}

const REPORTS = ["Property Summary", "Cash Flow", "Income & Expenses", "Maintenance",
                 "Insurance", "Mortgage", "Tax", "Portfolio Comparison"];

export function ReportsTab() {
  return (
    <SectionCard title="Reports">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reports-grid">
        {REPORTS.map((r) => (
          <div key={r} className="bg-white/60 border border-[#EAE0D2] rounded-xl p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileBarChart size={18} className="text-[#C6A45C]" />
              <span className="text-[#2C2A28] text-sm">{r}</span>
            </div>
            <span className="text-[10px] uppercase tracking-[0.1em] text-[#A99F90]">Soon</span>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

export function SettingsTab({ property, options, reload, onDeleted }) {
  const [state, setState] = useState({
    nickname: property.nickname, address: property.address || "",
    purchase_date: property.purchase_date || "", purchase_price: property.purchase_price || 0,
    estimated_market_value: property.estimated_market_value || 0,
    occupancy_status: property.occupancy_status || "Tenanted",
  });
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/properties/${property.id}`, {
        ...state, purchase_price: Number(state.purchase_price) || 0,
        estimated_market_value: Number(state.estimated_market_value) || 0,
      });
      toast.success("Property updated"); reload();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    await api.delete(`/properties/${property.id}`);
    toast.success("Property removed"); onDeleted();
  };

  return (
    <SectionCard title="Property settings">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TField label="Nickname" value={state.nickname} onChange={(v) => set("nickname", v)} testid="settings-nickname" />
        <TField label="Full address" value={state.address} onChange={(v) => set("address", v)} testid="settings-address" />
        <TField label="Purchase date" type="date" value={state.purchase_date} onChange={(v) => set("purchase_date", v)} testid="settings-purchase-date" />
        <TField label="Purchase price" type="number" value={state.purchase_price} onChange={(v) => set("purchase_price", v)} testid="settings-purchase-price" />
        <TField label="Estimated market value" type="number" value={state.estimated_market_value} onChange={(v) => set("estimated_market_value", v)} testid="settings-market-value" />
        <TSelect label="Occupancy status" value={state.occupancy_status} options={options.occupancy || []} onChange={(v) => set("occupancy_status", v)} testid="settings-occupancy" />
      </div>
      <div className="mt-6 flex items-center justify-between">
        <GoldButton data-testid="settings-save" onClick={save} disabled={saving}>
          {saving ? <Loader2 size={15} className="animate-spin" /> : null} Save changes
        </GoldButton>
        <OutlineButton data-testid="settings-delete" onClick={() => setConfirm(true)} className="text-[#b06b6b] border-[#C99FA0]/50"><Trash2 size={15} /> Remove property</OutlineButton>
      </div>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className={ui.dialog}>
          <DialogHeader><DialogTitle className="font-display text-2xl font-light">Remove this property?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6b6256]">Linked transactions and documents are kept in your records — only this property workspace is removed.</p>
          <DialogFooter>
            <OutlineButton onClick={() => setConfirm(false)}>Cancel</OutlineButton>
            <GoldButton data-testid="settings-delete-confirm" onClick={remove}>Remove</GoldButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}
